/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useReviewCandidates } from '../../../features/customReviews/api/getReviewCandidates';
import { OptionalSuspense } from '@/features/performance/components/OptionalSuspense';
import { CMSHomeSpacer } from '@/features/home/components/CMSHomeSpacer';
import { ReviewCandidates } from '../../../features/customReviews/components/ReviewCandidates/ReviewCandidates';
import type { ContentStackBanner } from '../../../features/contentstack/interface';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { memo, useEffect, useState } from 'react';
import { Skeleton } from '@az/starc-ui';
import styles from '../styles.module.scss';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAppData } from '@/hooks/useAppData';

type Props = {
  limit: number;
  title: string;
  fallbackBanner?: ContentStackBanner;
  shouldReduceSpacing?: boolean;
  customClass?: string;
};

export const ReviewCandidatesWithFallback = memo(function Component({
  limit,
  title,
  fallbackBanner,
  shouldReduceSpacing,
  customClass,
}: Props) {
  const [showReviewCandidates, setShowReviewCandidates] = useState(false);

  const { appData } = useAppData();
  const { userAuthenticated } = appData;

  const { data: reviewCandidatesData, isPending: reviewCandidatesPending } = useReviewCandidates({
    limit: limit + 1,
    enabled: userAuthenticated && limit > 0,
  });

  useEffect(() => {
    const showHomeReviewCandidates = sessionStorage.getItem('showHomeReviewCandidates') !== 'false';
    setShowReviewCandidates(showHomeReviewCandidates);
  }, []);

  const handleCloseReviewCandidates = () => {
    sessionStorage.setItem('showHomeReviewCandidates', 'false');
    setShowReviewCandidates(false);
  };

  const shouldDisplayContainer = userAuthenticated && showReviewCandidates;

  if (shouldDisplayContainer) {
    if (reviewCandidatesPending) {
      return (
        <CMSHomeSpacer>
          <Skeleton
            className={styles.skeleton}
            attributes={{ 'data-testid': 'action-candidates-skeleton' }}
          />
        </CMSHomeSpacer>
      );
    }

    if (reviewCandidatesData && reviewCandidatesData.productReviewCandidates.length > 0) {
      return (
        <OptionalSuspense>
          <CMSHomeSpacer removeVerticalSpacing removeHorizontalSpacing>
            <ReviewCandidates
              orders={reviewCandidatesData.productReviewCandidates}
              label={title}
              limit={limit}
              onClose={handleCloseReviewCandidates}
            />
          </CMSHomeSpacer>
        </OptionalSuspense>
      );
    }
  }
  if (fallbackBanner) {
    return (
      <OptionalSuspense>
        <CMSHomeSpacer
          shouldReduceSpacing={shouldReduceSpacing}
          className={customClass}
          {...(fallbackBanner.component_id && {
            id: fallbackBanner.component_id,
          })}
        >
          <CMSBanner content={fallbackBanner} />
        </CMSHomeSpacer>
      </OptionalSuspense>
    );
  }
  return null;
});
